import React from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { useApiCache } from "customHooks/useApi";
import { useAuthState } from "_ReactContext/AuthContext";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
    IInputProps,
    useRhfFieldWithoutControllerOrRegister
} from "customHooks/useCompleoReactHookForm/helpers/reactHookFormsHelper";

interface IProps {
    open: boolean;
    setOpen: any;
    t: any;
}

const FormDialog = (props: IProps) => {
    const { open, setOpen, t } = props;
    const [value, setValue] = React.useState<Compleo.IObject | null>(null);
    const [inputValue, setInputValue] = React.useState<string>("");
    const hasInitializedRef = React.useRef<boolean>(false);
    const { user, company } = useAuthState();
    const [templates, setTemplates] = React.useState<Compleo.IObject[]>([]);
    const { watch, setFieldValue } = useRhfFieldWithoutControllerOrRegister(
        "screeningQuestionnaire"
    );
    const fieldValue = watch("screeningQuestionnaire");

    const handleClose = () => {
        setOpen(false);
    };

    const [
        questionnaireList,
        fetchQuestionnaires,
        ,
        isFetchingQuestionnaires
    ] = useApiCache(
        "/questionnaire/list",
        "post",
        {
            companyId: company.companyId
        },
        false
    );
    const data = questionnaireList.response?.data?.data;

    const handleChangeTemplate = () => {
        if (!value) {
            setOpen(false);
            return;
        }

        const optionSelected = data?.find(
            (d: any) => d.skGS1pk === value.value
        );

        if (!optionSelected) {
            setOpen(false);
            return;
        }

        const newValue = { ...(fieldValue || {}) };
        newValue.name = optionSelected.name;
        newValue.startMessage = optionSelected.startMessage;
        newValue.enableStartMessage = optionSelected.enableStartMessage;
        newValue.enableResults = optionSelected.enableResults;
        newValue.results = optionSelected.results;
        newValue.id = optionSelected.skGS1pk;
        newValue.questions = optionSelected.questions;
        newValue.translations = optionSelected.translations;
        newValue.enableResults = optionSelected.enableResults;
        newValue.rankingToResults = optionSelected.rankingToResults;
        newValue.enableResultsByRankingScore =
            optionSelected.enableResultsByRankingScore;
        newValue.enableRanking = optionSelected.enableRanking;

        setFieldValue(newValue);
        setOpen(false);
    };

    // Effect A: update options when data changes
    React.useEffect(() => {
        if (!data) {
            setTemplates([]);
            return;
        }

        const normalizedTemplates: Compleo.IObject[] = data.map((d: any) => ({
            name: d.name,
            value: d.skGS1pk
        }));

        setTemplates(normalizedTemplates);
    }, [data]);

    // Effect B: set default selection only once when data first arrives
    React.useEffect(() => {
        if (hasInitializedRef.current || !data) return;

        const defaultTemplate = data.find((d: any) => d.default === true);
        if (defaultTemplate) {
            const defaultValue = {
                name: defaultTemplate.name,
                value: defaultTemplate.skGS1pk
            };
            setValue(defaultValue);
            setInputValue(defaultTemplate.name);
        }
        hasInitializedRef.current = true;
    }, [data]);

    const handleInputChange = (
        event: React.ChangeEvent<{}>,
        newInputValue: string,
        reason: string
    ) => {
        setInputValue(newInputValue);

        if (reason === "clear") {
            setValue(null);
        }

        const trimmedValue = newInputValue.trim();

        if (trimmedValue.length === 0) {
            fetchQuestionnaires({
                companyId: company.companyId
            });
            return;
        }

        fetchQuestionnaires({
            companyId: company.companyId,
            filters: [
                {
                    field: "name",
                    value: trimmedValue
                }
            ]
        });
    };

    return (
        <div>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby={t("chooseQuestionnaireTemplateTitle")}
            >
                <DialogTitle id="form-dialog-title">
                    {t("chooseQuestionnaireTemplateTitle")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("chooseQuestionnaireTemplateInstructions")}
                    </DialogContentText>
                    <Autocomplete
                        id="questionnaireTemplate"
                        options={templates}
                        value={value}
                        inputValue={inputValue}
                        getOptionSelected={(option, selected) =>
                            option && selected
                                ? option.value === selected.value
                                : false
                        }
                        getOptionLabel={(option) => option?.name || ""}
                        loading={Boolean(isFetchingQuestionnaires)}
                        // style={{ width: 300 }}
                        onChange={(event: any, newValue: any) => {
                            setValue(newValue);
                            setInputValue(newValue?.name || "");
                        }}
                        onInputChange={handleInputChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("questionnaireTemplate")}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary">
                        {t("buttonNoChooseAnotherTemplate")}
                    </Button>
                    <Button onClick={handleChangeTemplate} color="primary">
                        {t("buttonYesChooseAnotherTemplate")}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default FormDialog;
